import { ref, computed, inject, onMounted, onUnmounted } from 'vue';

// custom (Modeloja · Lote 9): 120s → 48h.
//
// O relógio mede TEMPO NA ETAPA — dias, na vida real: as esperas medidas no board vão de
// 171h a 243h. Um alarme de 2 minutos sobre uma grandeza de dias não é preferência
// apertada, é erro de unidade — ele dispara em 100% dos cards, 100% do tempo. E alarme que
// nunca está desligado não alarma nada: ensina a operação a ignorar o vermelho, que é o
// oposto de para que ele existe.
//
// 48h é o ponto onde "esse card parou" começa a ser verdade num funil de dias. É palpite
// calibrado, não medição — o número certo é decisão do dono, e a conta que já existe segue
// com o valor dela até ele escolher (ver a migração deste lote).
export const DEFAULT_THRESHOLD = 48 * 60 * 60;

// Symbol used to provide/inject the shared board-level tick.
export const BOARD_NOW_KEY = Symbol('crm-board-now');

// Symbol used to provide/inject the account-level threshold (from crm_settings).
export const BOARD_THRESHOLD_KEY = Symbol('crm-board-threshold');

/**
 * Board-level shared ticker (1 setInterval for the whole board).
 * Call this once in CrmBoard.vue and provide(BOARD_NOW_KEY, boardNow).
 */
export function useBoardTick() {
  const now = ref(Date.now());
  let timer = null;

  onMounted(() => {
    timer = setInterval(() => {
      now.value = Date.now();
    }, 1000);
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
  });

  return { now };
}

/**
 * Client-side wait clock for a single deal card.
 *
 * Reads the shared `now` ref injected from CrmBoard (1 timer for the whole
 * board instead of N timers — one per card). Zero server polling.
 *
 * Base: deal.entrou_etapa_em — when the deal entered its current stage.
 * Resets to ~0 when the deal is moved (CrmBoard updates entrou_etapa_em
 * optimistically, then confirms from the move_stage response).
 *
 * @param {Ref<object>} deal      - ref to deal with entrou_etapa_em
 * @param {number}      threshold - seconds before clock turns red (default 120)
 */
export function useWaitClock(deal, threshold = null) {
  // Prefer the board-level shared ticker; fall back to a local one if used
  // outside the board context (e.g. tests, story).
  const injectedNow = inject(BOARD_NOW_KEY, null);
  // Prefer board-provided threshold (from crm_settings.wait_clock_seconds);
  // fall back to the caller-supplied value, then the module default.
  const injectedThreshold = inject(BOARD_THRESHOLD_KEY, null);
  const resolvedThreshold =
    injectedThreshold?.value ?? threshold ?? DEFAULT_THRESHOLD;
  const localNow = ref(Date.now());
  let fallbackTimer = null;

  if (!injectedNow) {
    onMounted(() => {
      fallbackTimer = setInterval(() => {
        localNow.value = Date.now();
      }, 1000);
    });
    onUnmounted(() => {
      if (fallbackTimer) clearInterval(fallbackTimer);
    });
  }

  const now = injectedNow ?? localNow;

  const elapsedSeconds = computed(() => {
    const base = deal.value?.entrou_etapa_em
      ? new Date(deal.value.entrou_etapa_em).getTime()
      : null;
    if (!base) return 0;
    return Math.max(0, Math.floor((now.value - base) / 1000));
  });

  const isRed = computed(() => elapsedSeconds.value > resolvedThreshold);

  // custom (Modeloja · Lote 9): O SILÊNCIO COMO ESTADO.
  //
  // O relógio aparecia em TODO card aberto, sempre. Trinta cards, trinta números — e um
  // número que diz "está tudo bem" trinta vezes é trinta unidades de ruído: ocupa a mesma
  // linha, o mesmo peso e a mesma atenção do número que diz "este aqui parou".
  //
  // Agora ele só fala quando tem o que dizer. Abaixo do limiar, silêncio. Só se confia em
  // alarme que às vezes está desligado.
  //
  // A informação não se perde: o tempo na etapa segue no painel do card, que é onde se vai
  // quando a pergunta é "há quanto tempo isso está aqui?". O board responde outra pergunta
  // — "o que travou?" — e para essa, o card calado já é a resposta.
  const formatted = computed(() => {
    const s = elapsedSeconds.value;
    if (s <= 0) return null;
    if (!isRed.value) return null;
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    // custom (2026-08-23): A UNIDADE ROLA PARA DIAS EM 48h.
    //
    // A escada parava na hora e nunca subia. Como o relógio só fala ACIMA do limiar — 48h
    // por padrão — a hora era sempre a unidade escolhida, e ela estourava: a Home do
    // operador mostrava `2864h` e o board `1037h`. É o mesmo erro de unidade que o
    // comentário do topo deste arquivo condena, só que na SAÍDA em vez de na entrada:
    // ninguém lê 2864h como "quatro meses parado" sem parar para dividir, e o relógio
    // existe justamente para não exigir que se pare.
    //
    // 48h é o ponto de virada porque é o próprio limiar: abaixo dele o relógio está calado,
    // então hora só aparece em conta que apertou o `wait_clock_seconds` de propósito — e aí
    // a hora é mesmo a unidade certa. Acima, a operação pensa em dias, e a tela vizinha já
    // fala em dias ("há 5 d" nas listas da mesma Home).
    if (s < 48 * 3600) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
  });

  return { elapsedSeconds, isRed, formatted };
}
